'use client';

/**
 * @fileoverview Scrollable message list. Auto-scrolls to the bottom when
 * new messages arrive or the in-flight assistant message grows, unless
 * the user has scrolled up manually (in which case we respect their
 * position and show a "new messages" chip instead — simple v1 omits
 * the chip and just stops auto-scrolling).
 */

import { useEffect, useRef } from 'react';
import type { ChatMessage } from '@/domains/chat/schemas';
import type { StreamingAssistantMessage } from '@/domains/chat/hooks/use-chat-stream';
import { MessageBubble, StoredMessage } from './message-bubble';

interface MessageListProps {
  messages: ChatMessage[];
  streamingMessage: StreamingAssistantMessage | null;
  /** Shown when the session has no messages and nothing is streaming. */
  emptyState?: React.ReactNode;
}

export function MessageList({
  messages,
  streamingMessage,
  emptyState,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  // Track whether the user is near the bottom so we know whether to
  // auto-scroll on new content. Threshold is lenient (~80px) to avoid
  // being over-sticky when the user is mid-read.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll on new tokens / new turns when the user was at bottom.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streamingMessage?.content, streamingMessage?.toolCalls.length]);

  const hasContent = messages.length > 0 || streamingMessage;

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
    >
      {!hasContent && emptyState}

      {messages.map((m) => (
        <StoredMessage key={m.id} message={m} />
      ))}

      {streamingMessage && (
        <MessageBubble
          role="assistant"
          content={streamingMessage.content}
          toolCalls={streamingMessage.toolCalls}
          isStreaming
        />
      )}
    </div>
  );
}
